import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import { useDialog } from "~/hooks/use-dialog";
import { useOrganisation } from "~/contexts/organisation-provider";
import { toast } from "~/utils/toast";
import { Modal } from "~/components/modal/modal";
import { useSession } from "~/contexts/auth-context";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { Text } from "~/components/text";
import { logError } from "~/shared/services/error-reporting";

export const SwitchOrganisationModal = () => {
  const { t } = useTranslation(["settings"]);
  const [isSwitching, setIsSwitching] = useState(false);
  const dialog = useDialog("switch-organisation");
  const { organisation, transferUser } = useOrganisation();
  const { user, organisation: currentOrganisation } = useSession();

  const isCurrentOrg = currentOrganisation.id === organisation?.id;
  const isDeletedOrg = !!organisation?.deletedAt;
  const isDisabled = isCurrentOrg || isDeletedOrg;

  async function onConfirm() {
    if (isSwitching || !organisation) {
      return;
    }

    setIsSwitching(true);

    try {
      await transferUser(currentOrganisation.id, organisation.id, user.id);

      toast.success(t("settings:organisation.switch.success"));
      dialog.close();
    } catch (e) {
      logError(e);
      toast.error(t("settings:organisation.switch.error"));
    }

    setIsSwitching(false);
  }

  return (
    <>
      <Tooltip open={isCurrentOrg ? undefined : false}>
        <TooltipTrigger asChild>
          <span>
            <Button
              variant="tertiary"
              onClick={dialog.open}
              className="mx-4"
              icon="user-plus"
              disabled={isDisabled}
            >
              {t("settings:organisation.switch.button")}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <Text variant="body">
            {t("settings:organisation.switch.tooltip")}
          </Text>
        </TooltipContent>
      </Tooltip>

      <Modal
        id={dialog.id}
        open={dialog.isOpen}
        variant="warning"
        mainIconName="user-plus"
        title={t("settings:organisation.switch.title")}
        description={t("settings:organisation.switch.description", {
          name: organisation?.name,
        })}
        confirmLabel={t("settings:organisation.switch.actions.confirm")}
        cancelLabel={t("settings:organisation.switch.actions.cancel")}
        onOpenChange={dialog.onOpenChange}
        loading={isSwitching}
        onConfirm={() => onConfirm()}
      />
    </>
  );
};
